import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, Cpu, FileText, Clock } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { analyticsAPI } from '../../../utils/api';
import { getErrorMessage } from '../../../utils/errorTranslation';

const AIMetrics = ({ filters, showToast }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getAiMetrics({ period: filters.period });
      setData(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching AI metrics:', error);
      if (showToast) {
        showToast(getErrorMessage(error, t, t('dashboard.analytics.error.fetchFailed')), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [filters.period, showToast, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 h-full min-h-[320px]"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.analytics.ai.title')}</h3>
          <p className="text-sm text-gray-500">{t('dashboard.analytics.ai.subtitle')}</p>
        </div>
        <Brain className="h-6 w-6 text-primary-600" />
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : !data ? (
        <div className="text-sm text-gray-500">{t('dashboard.analytics.noData')}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center text-xs text-gray-500">
              <Cpu className="h-4 w-4 mr-1" />
              {t('dashboard.analytics.ai.analyses')}
            </div>
            <div className="text-lg font-semibold text-gray-900">{data.analyses || 0}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center text-xs text-gray-500">
              <Brain className="h-4 w-4 mr-1" />
              {t('dashboard.analytics.ai.scored')}
            </div>
            <div className="text-lg font-semibold text-gray-900">{data.scored || 0}%</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center text-xs text-gray-500">
              <FileText className="h-4 w-4 mr-1" />
              {t('dashboard.analytics.ai.summaries')}
            </div>
            <div className="text-lg font-semibold text-gray-900">{data.summaries || 0}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="h-4 w-4 mr-1" />
              {t('dashboard.analytics.ai.avgTime')}
            </div>
            <div className="text-lg font-semibold text-gray-900">{data.avgTime || '-'}</div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AIMetrics;
